/* eslint-disable @typescript-eslint/no-unused-vars */
import { makeAutoObservable, runInAction } from "mobx";
import { IOrder, IVWPost } from "@/interfaces";
import gql from "@/repo/apolloClient";
import { POST_STATUS, POST_TYPE } from "@/@types/EnumType";
import { ICreatePost } from "@/interfaces/post/post";
import RestAPIClient from "@/repo/restApi";
import { BigNumber } from "ethers";
import MarketContractStore from "../contract";
import { enqueueSnackbar } from "notistack";
interface TradingState {
  myOrder?: IOrder[];
  orders?: IOrder[];
  selectPost?: IVWPost;
  postsOpen?: IVWPost[];
  myPosts: IVWPost[];
  latestPosts?: IVWPost[];
}

export class TradingStore {
  private state: TradingState = { myPosts: [] };
  restApi: RestAPIClient<unknown>;
  marketContractStore: MarketContractStore;
  constructor(marketContractStore: MarketContractStore) {
    this.marketContractStore = marketContractStore;
    this.restApi = new RestAPIClient("cryptoMarketPlace");
    makeAutoObservable(this);
  }
  //#region Posts
  //#region actions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleMyPostsUpdate = async (response: any) => {
    const posts = response.data.vw_posts as Array<IVWPost>;

    const updatedPosts =
      (await Promise.all(
        posts.map(async (post) => {
          const contractPost = await this.marketContractStore.getPost(
            post.contract_post_id,
          );
          return {
            ...post,
            maxOrder: contractPost.maxOrder.toString(),
            minOrder: contractPost.minOrder.toString(),
          };
        }),
      ).catch((err) => {
        console.error("handleMyPostsUpdate", err);
        enqueueSnackbar("Failed to get posts data", { variant: "error" });
      })) ?? [];
    runInAction(() => {
      this.state.myPosts = updatedPosts as Array<IVWPost>;
    });
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleOpenPostsUpdate = async (response: any) => {
    const posts = response.data.vw_posts as Array<IVWPost>;

    const updatedPosts =
      (await Promise.all(
        posts.map(async (post) => {
          const contractPost = await this.marketContractStore.getPost(
            post.contract_post_id,
          );
          return {
            ...post,
            maxOrder: contractPost.maxOrder.toString(),
            minOrder: contractPost.minOrder.toString(),
          };
        }),
      ).catch((err) => {
        console.error("handleOpenPostsUpdate", err);
        enqueueSnackbar("Failed to get posts data", { variant: "error" });
      })) ?? [];
    runInAction(() => {
      this.state.postsOpen = updatedPosts.filter((post) =>
        BigNumber.from(post.token_amount).gt(0),
      ) as Array<IVWPost>;
    });
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleSelectedPostUpdate = async (response: any) => {
    const post = response.data.vw_posts[0] as IVWPost;
    const contractPost = await this.marketContractStore
      .getPost(post.contract_post_id)
      .catch((err) => {
        console.error("handleSelectedPostUpdate", err);
        enqueueSnackbar("Failed to get posts data", { variant: "error" });
      });

    const updatedPosts = {
      ...post,
      maxOrder: contractPost?.maxOrder.toString() ?? undefined,
      minOrder: contractPost?.minOrder.toString() ?? undefined,
    };

    runInAction(() => {
      this.state.selectPost = updatedPosts as IVWPost;
    });
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleLatestBuyPostsUpdate = async (response: any) => {
    const buyPosts = response.data.vw_posts as Array<IVWPost>;
    const updatedBuyPosts =
      (await Promise.all(
        buyPosts.map(async (post) => {
          const contractPost = await this.marketContractStore.getPost(
            post.contract_post_id,
          );
          return {
            ...post,
            maxOrder: contractPost.maxOrder.toString(),
            minOrder: contractPost.minOrder.toString(),
          };
        }),
      ).catch((err) => {
        console.error("handleLatestBuyPostsUpdate", err);
        enqueueSnackbar("Failed to get posts data", { variant: "error" });
      })) ?? [];

    runInAction(() => {
      const oldSellPosts =
        this.state.latestPosts?.filter((post) => post.post_type === "SELL") ??
        null;
      this.state.latestPosts = updatedBuyPosts.filter((post) =>
        BigNumber.from(post.token_amount).gt(0),
      );
      if (oldSellPosts && this.state.latestPosts) {
        this.state.latestPosts = this.state.latestPosts.concat(oldSellPosts);
      }
    });
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleLatestSellPostsUpdate = async (response: any) => {
    const sellPost = response.data.vw_posts as Array<IVWPost>;
    const updatedSellPost =
      (await Promise.all(
        sellPost.map(async (post) => {
          const contractPost = await this.marketContractStore.getPost(
            post.contract_post_id,
          );
          return {
            ...post,
            maxOrder: contractPost.maxOrder.toString(),
            minOrder: contractPost.minOrder.toString(),
          };
        }),
      ).catch((err) => {
        console.error("handleLatestSellPostsUpdate", err);
        enqueueSnackbar("Failed to get posts data", { variant: "error" });
      })) ?? [];

    runInAction(() => {
      const oldBuyPosts =
        this.state.latestPosts?.filter((post) => post.post_type === "BUY") ??
        null;
      this.state.latestPosts = updatedSellPost.filter((post) =>
        BigNumber.from(post.token_amount).gt(0),
      );
      if (oldBuyPosts && this.state.latestPosts) {
        this.state.latestPosts = this.state.latestPosts.concat(oldBuyPosts);
      }
    });
  };
  public createPost = async (request: ICreatePost): Promise<string> => {
    const mutation = `
     mutation createPost($obj: posts_insert_input!) {
       insert_posts_one(object: $obj) {
         post_id
         tx_hash
       }
      }
    `;
    const result = await gql.Mutation(mutation, { obj: request });
    await this.restApi.post({
      method: "updatePostIdFromTXHash",
      request: { input: { tx_hash: result.data.insert_posts_one.tx_hash } },
    });

    return result.data.insert_posts_one.post_id;
  };
  public updatePost = async (request: {
    updatedPost: ICreatePost;
    user_id: string;
    post_id: string;
  }) => {
    const queryUpdatePost = `
      mutation updatePost($obj: posts_set_input) {
        update_posts(
          where: {post_id: {_eq: "${request.post_id}"}},
          _set: $obj
        ) {
          affected_rows
        }
      }
    `;
    await gql.Mutation(queryUpdatePost, { obj: request.updatedPost });
    return true;
  };
  public removePost = async (request: { user_id: string; post_id: string }) => {
    const queryRemovePost = `
    mutation cancelPost{
      update_posts_by_pk(pk_columns:{post_id: "${request.post_id}"},_set:{status: "CANCELLED"}) {
        status
      }
    }    
    `;
    await gql.Mutation(queryRemovePost);
    return true;
  };
  public fetchPost = async (post_id: string): Promise<IVWPost> => {
    const query = `
      subscription getPost {
        vw_posts (where: {post_id: {_eq: "${post_id}"}}) {
          asset_name
          created_at
          currency
          deleted_at
          description
          max_amount
          min_amount
          payment_name
          post_id
          post_type
          status
          updated_at
          price
          payment_method
          asset_id
          title
          address
          token_amount
          tx_hash
          contract_post_id
        }
      }`;
    const result = await gql.Query(query);
    return result.data.vw_posts[0];
  };
  public subscribeToMyPosts = async (user_id: string) => {
    const query = `
    subscription getMyPosts {
        vw_posts (where: {created_by: {_eq: "${user_id}"}, tx_hash: {_is_null: false}, contract_post_id: {_is_null: false}},order_by:{created_at: desc}) {
          asset_name
          created_at
          currency
          deleted_at
          description
          max_amount
          min_amount
          payment_name
          post_id
          post_type
          status
          updated_at
          price
          payment_method
          asset_id
          title
          address
          token_amount
          tx_hash
          contract_post_id
          orderavariable
        }
      }`;

    const subscription = (await gql.Subscribe(query)).subscribe({
      next: this.handleMyPostsUpdate,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      error: (error: any) =>
        console.error("Error fetching open posts: ", error),
    });

    return () => subscription.unsubscribe();
  };
  public subscribeToOpenPosts = async () => {
    const query = `
      subscription {
        vw_posts(where: {status: {_eq: "${POST_STATUS.ACTIVE}"},tx_hash: {_is_null: false},contract_post_id: {_is_null: false}}) {
          post_id
          title
          post_type
          asset_name
          min_amount
          max_amount
          currency
          payment_name
          payment_method
          status
          price
          token_amount
          tx_hash
          contract_post_id
          created_by
          asset_id       
          token_amount
          orderavariable
        }
      }`;

    const subscription = (await gql.Subscribe(query)).subscribe({
      next: this.handleOpenPostsUpdate,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      error: (error: any) =>
        console.error("Error fetching open posts: ", error),
    });

    return () => subscription.unsubscribe();
  };

  public subscribeToLatestPosts = async () => {
    const postTypeBuy: POST_TYPE = "BUY";
    const queryBuy = `
    subscription getLatestPosts {
        vw_posts(order_by: {created_at: desc}, limit: 5, where: {post_type: {_eq: "${postTypeBuy}" }, status: {_eq: "${POST_STATUS.ACTIVE}"},tx_hash: {_is_null: false},contract_post_id: {_is_null: false}}) {
          post_id
          title
          post_type
          created_at
          asset_name
          min_amount
          max_amount
          currency
          payment_name
          status
          price
          token_amount
          tx_hash
          contract_post_id
          orderavariable
        }
      }`;
    const postTypeSell: POST_TYPE = "SELL";
    const querySell = `
    subscription getLatestPosts {
        vw_posts(order_by: {created_at: desc}, limit: 5, where: {post_type: {_eq: "${postTypeSell}" }, status: {_eq: "${POST_STATUS.ACTIVE}"},tx_hash: {_is_null: false},contract_post_id: {_is_null: false}}) {  
          post_id
          title
          post_type
          asset_name
          min_amount
          max_amount
          currency
          payment_name
          status
          price
          token_amount
          tx_hash
          contract_post_id
          orderavariable
        }
      }`;
    const subscriptionBuy = (await gql.Subscribe(queryBuy)).subscribe({
      next: this.handleLatestBuyPostsUpdate,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      error: (error: any) =>
        console.error(`Error fetching latest buy posts: ${error}`),
    });
    const subscriptionSell = (await gql.Subscribe(querySell)).subscribe({
      next: this.handleLatestSellPostsUpdate,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      error: (error: any) =>
        console.error(`Error fetching latest sell posts: ${error}`),
    });

    return () => {
      subscriptionBuy.unsubscribe();
      subscriptionSell.unsubscribe();
    };
  };
  public subscribeToPost = async (post_id: string) => {
    const query = `
    subscription getPost {
        vw_posts(where: {post_id: {_eq: "${post_id}"},tx_hash: {_is_null: false},contract_post_id: {_is_null: false}}) {
          post_id
          title
          post_type
          asset_name
          min_amount
          max_amount
          currency
          payment_name
          payment_method
          status
          price
          token_amount
          tx_hash
          contract_post_id
          created_by
          asset_id
          address          
          token_amount
          firstname
          lastname
          phone_number
          orderavariable
        }
      }`;
    const subscription = (await gql.Subscribe(query)).subscribe({
      next: this.handleSelectedPostUpdate,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      error: (error: any) => console.error("Error fetching post: ", error),
    });
    //TODO: get post from contract
    return () => {
      subscription.unsubscribe();
      runInAction(() => {
        this.state.selectPost = undefined;
      });
    };
  };

  //#endregion
  //#region getters
  public get selectedPost() {
    return this.state.selectPost;
  }
  public get myPosts() {
    return this.state.myPosts;
  }
  public get postsOpen() {
    return this.state.postsOpen;
  }
  public get latestPosts() {
    return this.state.latestPosts;
  }
  public get postsOpenSell() {
    let postsOpenSell = this.state.postsOpen;
    if (postsOpenSell) {
      postsOpenSell = postsOpenSell.filter((f) => f.post_type === "SELL");
    } else {
      postsOpenSell = [];
    }
    return postsOpenSell;
  }
  public get postsOpenBuy() {
    let postsOpenBuy = this.state.postsOpen;
    if (postsOpenBuy) {
      postsOpenBuy = postsOpenBuy.filter((f) => f.post_type === "BUY");
    } else {
      postsOpenBuy = [];
    }
    return postsOpenBuy;
  }
  public getPost = async (post_id: string): Promise<IVWPost | null> => {
    const query = `
      query getPost {
        vw_posts(where: {post_id: {_eq: "${post_id}"},tx_hash: {_is_null: false},contract_post_id: {_is_null: false}}, order_by:{created_at: desc}) {
          post_id
          title
          post_type
          asset_name
          min_amount
          max_amount
          currency
          payment_name
          payment_method
          status
          price
          token_amount
          tx_hash
          contract_post_id
          created_by
          asset_id
          created_at
          address          
          token_amount
          firstname
          lastname
          phone_number
          orderavariable
        }
      }`;
    const result = await gql.Query(query);
    return result.data.vw_posts[0] ?? null;
  };
  //#endregion
  //#endregion
}
